import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Tabs } from '@praktio/common-ui';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Col } from 'react-bootstrap';

import { updateUser } from '../../ducks/users';
import { updateSettings } from '../../ducks/settings';
import PageHeader from '../common/PageHeader/PageHeader';
import SystemSettings from './SystemSettings/SystemSettings';

import './Settings.scss';
import NetworkErrorModal from '../common/modals/NetworkErrorModal/NetworkErrorModal';
import FeedbackModal from '../common/FeedbackModal/FeedbackModal';
import UserSettingsContent from '../learner/Settings/SettingsContent';
import { RootState } from '../../ducks/store';
import { isPlatformAdmin as checkPlatformAdmin } from '../../utils/functions';

const Settings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userSettingsContentRef = useRef<IRemoteUserSettingsFunctionsProps>(null);
  const systemSettingsRef = useRef<IRemoteSystemSettingsFunctionsProps>(null);
  const platformSettingsRef = useRef<IRemoteSystemSettingsFunctionsProps>(null);

  const { isPlatformAdmin } = useSelector((state: RootState) => {
    const { authenticatedUser } = state.auth;
    const isPlatformAdmin = checkPlatformAdmin(authenticatedUser);
    return {
      isPlatformAdmin,
    };
  });

  const [userHasChanges, setUserHasChanges] = useState(false);
  const [settingsHasChanges, setSettingsHasChanges] = useState(false);
  const [platformSettingsHasChanges, setPlatformSettingsHasChanges] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const [savedChanges, setSavedChanges] = useState(false);
  const [showNetworkErrorModal, setShowNetworkErrorModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [currentError, setCurrentError] = useState<string | undefined>();

  // Handle disable state of Save button and
  // the appearance of the Saved badge.
  useEffect(() => {
    const hasChanges = userHasChanges || settingsHasChanges || platformSettingsHasChanges;
    if (hasChanges && savedChanges) {
      setSavedChanges(false);
    }
  }, [userHasChanges, settingsHasChanges, platformSettingsHasChanges, savedChanges]);

  const handleNetworkError = (error: IError) => {
    setShowNetworkErrorModal(true);
    setCurrentError(error.message);
  };

  const handleSubmit = async () => {
    setDisableSave(true);

    try {
      const validatedUserData = await userSettingsContentRef.current?.validateData();
      const systemNotificationsData = systemSettingsRef.current?.getData();
      let platformData;
      if (isPlatformAdmin) {
        platformData = platformSettingsRef.current?.getData();
      }

      if (validatedUserData?.formData && systemNotificationsData?.formData) {
        if (validatedUserData?.hasChanges) {
          await dispatch(updateUser(validatedUserData.formData, true, navigate));
        }
        if (systemNotificationsData?.hasChanges) {
          await dispatch(updateSettings(systemNotificationsData.formData, false));
        }

        if (isPlatformAdmin && platformData?.hasChanges) {
          await dispatch(updateSettings(platformData.formData, true));
        }

        setSavedChanges(true);
      }
    } catch (e) {
      userSettingsContentRef.current?.handleError(e as IError);
    }
    setDisableSave(false);
  };

  const tabs = [
    {
      title: 'User Settings',
      key: 'userSettings',
      content: (
        <UserSettingsContent
          ref={userSettingsContentRef}
          notifyHasChanges={setUserHasChanges}
          handleNetworkError={handleNetworkError}
        />
      ),
    },
    {
      title: 'System Settings',
      key: 'systemSettings',
      content: <SystemSettings ref={systemSettingsRef} notifyHasChanges={setSettingsHasChanges} />,
    },
  ];

  /*
  // TODO: Commenting this out for now, reintroduce for multi-tenancy
  if (isPlatformAdmin) {
    tabs.push({
      title: 'Platform Settings',
      key: 'platformSettings',
      content: (
        <SystemSettings
          isPlatform
          ref={platformSettingsRef}
          notifyHasChanges={setPlatformSettingsHasChanges}
        />
      ),
    });
  }
  */

  return (
    <div className="AdminSettings">
      <Helmet>
        <title>Admin Settings - Praktio</title>
      </Helmet>
      <PageHeader
        pageName="Settings"
        unsavedChanges={userHasChanges || settingsHasChanges || platformSettingsHasChanges}
        savedChanges={savedChanges}
      >
        <Button
          btnStyle="primary"
          className="form-submit-button"
          disabled={
            disableSave ||
            (!userHasChanges &&
              !settingsHasChanges &&
              (!isPlatformAdmin || !platformSettingsHasChanges))
          }
          onClick={async () => await handleSubmit()}
        >
          SAVE
        </Button>
      </PageHeader>
      <div className="AdminSettingsTabs-wrapper container-fluid px-lg-5">
        <Col xs={12} md={8} lg={6}>
          <Tabs defaultActiveKey="userSettings" id="admin-settings-tabs" tabs={tabs} />
        </Col>
      </div>
      <NetworkErrorModal
        show={showNetworkErrorModal}
        onHide={() => setShowNetworkErrorModal(false)}
        submitFeedback={() => {
          setShowNetworkErrorModal(false);
          setShowFeedbackModal(true);
        }}
      />
      <FeedbackModal
        show={showFeedbackModal}
        onHide={() => setShowFeedbackModal(false)}
        errorMessage={currentError}
      />
    </div>
  );
};

export default Settings;
